/**
 * Orphaned Index Cleanup — two-pass mark-and-sweep GC for index tables.
 *
 * Removes `startAtIndex` and `privateIndex` entries whose parent task
 * no longer exists in the `totodo` table (e.g. permanently deleted,
 * sync conflict, or schema migration artifact).
 *
 * SAFETY MODEL (inspired by Git GC, AWS S3 lifecycle):
 *
 *   Pass 1 ("mark"):  Detect orphans → store candidate IDs + detection
 *                     timestamp in `setting_local` (never synced).
 *
 *   Pass 2 ("sweep"): On NEXT app-start (or later), re-check candidates.
 *                     Only delete entries that:
 *                     ① Still have no parent task
 *                     ② Were first detected ≥ GRACE_PERIOD_MS ago
 *                     ③ Sync is fully caught up (in-sync phase)
 *                     ④ Max BLAST_RADIUS_CAP entries per run
 *
 * This ensures transient sync states (partial pull, offline, slow network)
 * never cause false-positive deletions. An entry must be "dead" across
 * two separate app sessions spanning at least 3 days.
 *
 * Both `startAtIndex` and `privateIndex` are synced tables — deletions
 * propagate to ALL devices. Hence the extreme caution.
 */

import { db } from "@/db/schema"
import type { BackgroundTaskDef } from "@/background/types"
import { waitForInitialSync } from "@/shared/utils/sync/waitForInitialSync"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BATCH_SIZE = 200

/** Minimum time an entry must be orphaned before deletion (3 days). */
const GRACE_PERIOD_MS = 3 * 24 * 60 * 60 * 1000

/** Maximum entries deleted per run — limits blast radius of any bug. */
const BLAST_RADIUS_CAP = 50

/** setting_local key for persisting orphan candidates between sessions. */
const CANDIDATES_KEY = "#orphan-gc-candidates"

// ---------------------------------------------------------------------------
// Candidate persistence (local-only, never synced)
// ---------------------------------------------------------------------------

interface OrphanCandidate {
  id: string
  table: "startAtIndex" | "privateIndex"
  detectedAt: number
}

async function loadCandidates(): Promise<OrphanCandidate[]> {
  try {
    const row = await db.setting_local.get(CANDIDATES_KEY)
    if (row?.value) return JSON.parse(row.value)
  } catch {
    // Corrupt or missing — start fresh
  }
  return []
}

async function saveCandidates(candidates: OrphanCandidate[]): Promise<void> {
  await db.setting_local.put({
    id: CANDIDATES_KEY,
    value: JSON.stringify(candidates),
    device: "local",
    createdAt: new Date().toISOString(),
    createdBy: "system",
  })
}

// ---------------------------------------------------------------------------
// Pass 1: Mark — detect new orphans, add to candidate list
// ---------------------------------------------------------------------------

async function markOrphans(
  signal: AbortSignal,
  existing: OrphanCandidate[],
): Promise<OrphanCandidate[]> {
  const existingIds = new Set(existing.map((c) => c.id))
  const now = Date.now()
  const newCandidates: OrphanCandidate[] = []

  // Scan startAtIndex
  await scanTable("startAtIndex", (entryId) => {
    if (!existingIds.has(entryId)) {
      newCandidates.push({ id: entryId, table: "startAtIndex", detectedAt: now })
    }
  }, signal)

  // Scan privateIndex
  await scanTable("privateIndex", (entryId) => {
    if (!existingIds.has(entryId)) {
      newCandidates.push({ id: entryId, table: "privateIndex", detectedAt: now })
    }
  }, signal)

  return [...existing, ...newCandidates]
}

async function scanTable(
  tableName: "startAtIndex" | "privateIndex",
  onOrphan: (entryId: string) => void,
  signal: AbortSignal,
): Promise<void> {
  let offset = 0

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (signal.aborted) break

    const entries = await db[tableName]
      .offset(offset)
      .limit(BATCH_SIZE)
      .toArray()

    if (entries.length === 0) break

    const validEntries = entries.filter(
      (e): e is typeof e & { id: string } => !!e.id,
    )
    const taskIds = validEntries.map((e) => e.id.slice(1))
    const tasks = await db.totodo.bulkGet(taskIds)

    for (let i = 0; i < validEntries.length; i++) {
      const entry = validEntries[i]
      if (!tasks[i] && entry) {
        onOrphan(entry.id)
      }
    }

    offset += BATCH_SIZE
    await new Promise((r) => setTimeout(r, 10))
  }
}

// ---------------------------------------------------------------------------
// Pass 2: Sweep — delete entries that are STILL orphaned past grace period
// ---------------------------------------------------------------------------

async function sweepConfirmedOrphans(
  candidates: OrphanCandidate[],
  signal: AbortSignal,
): Promise<{ remaining: OrphanCandidate[]; deleted: number }> {
  const now = Date.now()
  const remaining: OrphanCandidate[] = []
  const toDeleteStartAt: string[] = []
  const toDeletePrivate: string[] = []
  let deleted = 0

  for (const candidate of candidates) {
    if (signal.aborted) {
      remaining.push(candidate)
      continue
    }

    // Not past grace period yet — keep in candidates
    if (now - candidate.detectedAt < GRACE_PERIOD_MS) {
      remaining.push(candidate)
      continue
    }

    // Blast radius cap reached — defer remaining to next run
    if (deleted >= BLAST_RADIUS_CAP) {
      remaining.push(candidate)
      continue
    }

    // Re-verify: is the task STILL missing?
    const taskId = candidate.id.slice(1) // Remove "#" prefix
    const task = await db.totodo.get(taskId)

    if (task) {
      // Task reappeared (sync caught up) — remove from candidates, do NOT delete
      continue
    }

    // Confirmed orphan — schedule for deletion
    if (candidate.table === "startAtIndex") {
      toDeleteStartAt.push(candidate.id)
    } else {
      toDeletePrivate.push(candidate.id)
    }
    deleted++
  }

  // Perform actual deletions
  if (toDeleteStartAt.length > 0) {
    await db.startAtIndex.bulkDelete(toDeleteStartAt)
  }
  if (toDeletePrivate.length > 0) {
    await db.privateIndex.bulkDelete(toDeletePrivate)
  }

  return { remaining, deleted }
}

// ---------------------------------------------------------------------------
// Task definition
// ---------------------------------------------------------------------------

export const orphanedIndexCleanup: BackgroundTaskDef = {
  id: "orphaned-index-cleanup",
  description:
    "Two-pass mark-and-sweep GC for orphaned startAtIndex/privateIndex entries",
  priority: 10,
  triggers: ["app-start"],
  initialDelayMs: 10_000, // 10s after app start

  async run(signal) {
    // Gate 1: Wait for sync to reach a stable state
    const synced = await waitForInitialSync(db)
    if (!synced || signal.aborted) return

    // Gate 2: Only sweep when FULLY in-sync (not error/offline)
    const syncPhase = db.cloud.syncState?.value?.phase
    const isFullyInSync = syncPhase === "in-sync"

    // Load existing candidates from previous sessions
    let candidates = await loadCandidates()

    // Pass 1: Mark new orphans (always runs — safe, just writes to setting_local)
    candidates = await markOrphans(signal, candidates)

    // Pass 2: Sweep confirmed orphans (only if fully in-sync)
    let deleted = 0
    if (isFullyInSync && !signal.aborted) {
      const result = await sweepConfirmedOrphans(candidates, signal)
      candidates = result.remaining
      deleted = result.deleted
    }

    // Persist updated candidate list
    await saveCandidates(candidates)

    if (deleted > 0 || candidates.length > 0) {
      console.info(
        `[orphanedIndex] Sweep: deleted ${deleted}, ` +
          `candidates pending: ${candidates.length} ` +
          `(grace: ${Math.round(GRACE_PERIOD_MS / 86400000)}d, ` +
          `cap: ${BLAST_RADIUS_CAP}/run, sync: ${syncPhase})`,
      )
    }
  },
}
